import { useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { mergeOpsApiRef } from '../api/MergeOpsApi';
import { PrSummary } from '../api/types';

/**
 * Human-readable message for an approve/merge failure. The raw error from the
 * backend client is a `ResponseError` whose `.message` is the unhelpful
 * "Request failed with 403 ..." wrapper; the useful text (which carries
 * GitHub's own reason) lives in the serialized backend error body. Prefer that,
 * and for the auth/permission statuses add a short hint about what to do.
 */
export function actionErrorMessage(err: unknown): string {
  const status = err instanceof ResponseError ? err.response.status : undefined;
  const backendMsg =
    err instanceof ResponseError
      ? (err.body as { error?: { message?: string } } | undefined)?.error
          ?.message
      : undefined;
  const base = backendMsg || (err instanceof Error ? err.message : String(err));
  if (status === 401) {
    return `${base} (GitHub と連携してから再試行してください)`;
  }
  if (status === 403) {
    return `${base} (権限不足か、必須チェック/ブランチ保護が未完了の可能性があります。CI 完了後に再試行するか、リポジトリへの write 権限・組織での OAuth App 許可を確認してください)`;
  }
  return base;
}

export type PrBusyState = 'approve' | 'merge' | null;

export interface PrActionHandlers {
  /** Called after a successful approve (PR stays open). */
  onApproved?: () => void;
  /** Called after a successful merge (PR is gone). */
  onMerged?: () => void;
}

export interface UsePrActions {
  busy: PrBusyState;
  error: Error | null;
  successMsg: string | null;
  approve: () => Promise<void>;
  merge: () => Promise<void>;
}

/**
 * Shared Approve / Merge action logic used by both the per-component PR rows
 * and the group-level sections. Callers handle UI updates via the handlers
 * (e.g. an optimistic local update) instead of a blanket refetch.
 */
export function usePrActions(
  componentName: string,
  pr: PrSummary,
  handlers: PrActionHandlers,
): UsePrActions {
  const api = useApi(mergeOpsApiRef);
  const [busy, setBusy] = useState<PrBusyState>(null);
  const [error, setError] = useState<Error | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const approve = async () => {
    setBusy('approve');
    setError(null);
    setSuccessMsg(null);
    try {
      await api.approvePr(componentName, pr.number);
      setSuccessMsg(`Approved #${pr.number}`);
      handlers.onApproved?.();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  };

  const merge = async () => {
    setBusy('merge');
    setError(null);
    setSuccessMsg(null);
    try {
      const result = await api.mergePr(componentName, pr.number, pr.title);
      const sha = result.sha ? ` (${result.sha.slice(0, 7)})` : '';
      setSuccessMsg(
        result.alreadyMerged
          ? `#${pr.number} was already merged${sha}`
          : `Merged #${pr.number}${sha}`,
      );
      handlers.onMerged?.();
    } catch (err) {
      setError(err as Error);
    } finally {
      setBusy(null);
    }
  };

  return { busy, error, successMsg, approve, merge };
}

// Shown when an action button is disabled because the signed-in user isn't a
// member of the owning team (see useOwnsEntity). Kept here so every merge
// button surfaces the same wording.
export const NOT_OWNER_REASON = 'You are not a member of the owning team';

export function mergeBlockReason(pr: PrSummary): string | null {
  // The merge runs as the signed-in user, so mirror GitHub's own merge button:
  // decide on `mergeableState` (GitHub's mergeStateStatus, which only weighs the
  // *required* checks/reviews), NOT the raw status-check rollup. The rollup
  // reads 'pending'/'failure' whenever an optional or non-required check is
  // still running or red, even though GitHub would happily let a user merge —
  // blocking on it produced false "Checks still pending" on green PRs.
  // The deploy image isn't in Artifact Registry yet — merging would deploy a
  // missing image. Highest-priority block (independent of GitHub mergeability).
  if (pr.imageMissing) {
    return 'Image not in Artifact Registry';
  }
  if (pr.approvals < pr.requiredApprovals) {
    return `Needs ${pr.requiredApprovals} approval(s)`;
  }
  switch (pr.mergeableState) {
    case 'dirty':
      return 'Merge conflicts';
    case 'behind':
      return 'Branch is out of date';
    case 'draft':
      return 'PR is a draft';
    // Required checks or reviews aren't satisfied — GitHub would reject the
    // merge with 405, so disable it up front.
    case 'blocked':
      return 'Required checks or reviews pending';
    // clean / has_hooks / unstable / unknown: GitHub allows a user to merge.
    // 'unstable' is the yellow "merge anyway" (a non-required check is red);
    // 'unknown' is GitHub still computing — let the user try (the backend
    // surfaces a clear reason if GitHub ultimately rejects it).
    default:
      return null;
  }
}
